import { useState } from 'react';
import { useReduxState } from 'hooks';
import { isURL } from 'validator';
import { scrollToElement } from 'lib/common';
import { Form, Text } from 'components/common/form';
import { EditorBlockTranslationReplace } from 'components/editor/blocks';
import { EditorBlockEmbedPreview } from '.';

import type { BlockProps } from 'components/editor/blocks/EditorBlock';
import type { BlockEmbedStateProps } from './EditorBlockEmbed';

const EditorBlockEmbedLink = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps & BlockEmbedStateProps) => {
  const { editorLanguageActive } = stateProps;

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const [showLanguageSpecificLink, setShowLanguageSpecificLink] =
    useState(false);

  const handleCancel = () => {
    setShowLanguageSpecificLink(false);
  };

  const handleIframePaste: React.ClipboardEventHandler<HTMLInputElement> = (
    ev
  ) => {
    const pastedUrl = ev.clipboardData.getData('Text');
    if (isURL(pastedUrl, { protocols: ['http', 'https'] })) {
      const text = `<iframe src="${pastedUrl}" style="width:100%; height:100%" frameborder="0" />`;

      updateData({
        ...data,
        locales: {
          ...data.locales,
          [languageVersion]: {
            ...data.locales[languageVersion],
            text: text,
            url: pastedUrl,
            translatable: {
              ...data.locales[languageVersion].translatable
            }
          }
        }
      });
      setShowLanguageSpecificLink(false);
    }
  };

  const handleRemove = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          text: null,
          url: null
        }
      }
    });
  };

  const handleReplace = () => {
    setShowLanguageSpecificLink(true);

    setTimeout(() => {
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;
      if (element) {
        const elementTop = element.getBoundingClientRect().top;
        if (elementTop < 0) scrollToElement({ element, offset: 200 });
      }
    }, 100);
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  const activeUrl = data.locales?.[languageVersion]?.url;
  const defaultUrl = data.locales?.[course.language]?.url;
  const url = activeUrl || defaultUrl;

  if (!url && isPreview) return null;

  return (
    <div className="embed">
      {!url || showLanguageSpecificLink ? (
        <div className="paste-handler">
          <div className="paste-label">
            {messages.editorBlockEmbedLinkPasteLabel}
          </div>

          <Form>
            <Text
              field="embedLink"
              id={`embed-link-paste-url-${blockId}`}
              inputStyle={{ textAlign: 'center' }}
              onPaste={handleIframePaste}
              placeholder={messages.editorBlockEmbedLinkPastePlaceholder}
            />
          </Form>
        </div>
      ) : (
        <div className="inner">
          <div className="title">
            {`${messages.blocksEmbed} ${messages.blocksEmbedLink}`}
          </div>

          <div className="link">{url}</div>

          <EditorBlockEmbedPreview
            blockId={blockId}
            blockType="embedlink"
            data={data}
            dataFromDefaultLanguage={!activeUrl}
            hideNonTranslatableSettings={isEditingLanguageVersion && !activeUrl}
            isPreview={isPreview}
            languageVersion={languageVersion}
            loading={false}
            updateData={updateData}
            updateOnChange={data.locales[languageVersion].text}
            {...stateProps}
          />
        </div>
      )}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksEmbedLinkUsingLanguageSpecific}
          defaultStatusText={messages.blocksEmbedLinkUsingDefaultLanguage}
          hasCustomValue={Boolean(activeUrl)}
          icon="link"
          onCancel={handleCancel}
          onRemove={handleRemove}
          onReplace={handleReplace}
          showCancel={!url || showLanguageSpecificLink}
        />
      ) : null}

      <style jsx>{`
        .embed {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .paste-handler {
          padding: 15px;
        }
        .inner {
          padding: 15px;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          padding: 0 0 5px;
        }
        .link {
          background-color: ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
          padding: 5px 10px;
          word-break: break-all;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockEmbedLink;
